/**
 * PLAYLISTS domain state — named, ordered collections of the user's own songs.
 *
 * Modelled on songsStore, deliberately and closely: a flat `create<State>` with
 * single-domain mutations, a fire-and-forget `void playlistsApi.x(...)` after each
 * one, `hydrate`/`reset`, and pure selector *functions* exported at the bottom
 * rather than selector methods on the state. Anything that spans domains —
 * removing a deleted song from every playlist, playing a playlist through the
 * player — is a hook (see hooks/usePlaylists.ts), which is the division of labour
 * stores/README.md and hooks/index.ts both state.
 *
 * WHAT A PLAYLIST HOLDS. Ids, not songs. See the note on the `Playlist` type: a
 * playlist that copied titles would show a stale one after a rename. Every read
 * resolves ids against songsStore, which is why nothing here has to be kept in
 * sync with the library.
 *
 * SIMULATED only in the sense that everything else is: playlists are in-memory and
 * are lost on reload, because playlistsApi is a mock. The behaviour itself is real —
 * the songs are the user's real songs and the ordering is really theirs. There is no
 * generation, no model and no audio involved in a playlist, so unlike the Studio's
 * AI tools there is nothing here standing in for a capability that does not exist.
 */
import { create } from 'zustand';
import { playlistsApi } from '../api/playlistsApi';
import { newId } from '../lib/ids';
import { parseLength, formatTime } from '../lib/formatting';
import type { Playlist, Song } from '../types/domain';
import { IDLE_LOAD, LOADING_LOAD, READY_LOAD, failedLoad, type LoadState } from './loadState';

/** Display date for a playlist created in this session. Matches songsStore's copy. */
const NOW_LABEL = 'Just now';

interface PlaylistsState {
  playlists: Playlist[];
  /** Songs referenced by playlists, including saved public songs outside the owner library. */
  playlistSongs: Song[];
  /** Monotonic ordering counter. Higher = newer. */
  seq: number;
  /**
   * Where the playlist READ has got to. See stores/loadState.ts.
   *
   * IT MATTERS MORE HERE THAN ANYWHERE ELSE IN THE APP, because this store starts EMPTY
   * and its empty state is a designed screen. `playlists.length === 0` alone cannot tell
   * "you have not made a playlist yet" from "the fetch has not returned", so the
   * Playlists page would render "Create your first playlist" over three playlists that
   * are about to arrive. `isConfirmedEmpty` is the predicate that fixes it.
   */
  load: LoadState;

  /** Creates a playlist and returns it, so the caller can navigate to it. */
  createPlaylist: (name: string, songIds?: string[]) => Playlist;
  rememberPlaylistSong: (song: Song) => void;
  renamePlaylist: (id: string, name: string) => void;
  deletePlaylist: (id: string) => void;
  /** Appends a song. A no-op if the playlist already contains it. */
  addSongToPlaylist: (playlistId: string, songId: string) => void;
  removeSongFromPlaylist: (playlistId: string, songId: string) => void;
  /** Moves a song one place earlier or later. A no-op at the ends. */
  moveSongInPlaylist: (playlistId: string, songId: string, direction: -1 | 1) => void;
  /** Drops a song from every playlist. Called when the song itself is deleted. */
  removeSongEverywhere: (songId: string) => void;
  /**
   * Fetches the playlists through `playlistsApi.getPlaylists()`.
   *
   * The read that had no caller. `getPlaylists()` returns the empty set — deliberately,
   * see its own comment — so today this load resolves to exactly what the store already
   * holds, and the value of having it is entirely in the seam and the state: `'ready'`
   * with nothing in it is a CONFIRMED empty library, which is a different fact from the
   * `'idle'` the store starts in, and the one the empty screen should be gated on.
   *
   * Never rejects; failures land in `load.error`.
   */
  loadPlaylists: () => Promise<void>;
  loadPlaylistSongs: () => Promise<void>;
  /** Replaces the set, e.g. from a future server fetch. */
  hydrate: (playlists: Playlist[]) => void;
  hydratePlaylistSongs: (songs: Song[]) => void;
  reset: () => void;
}

export const usePlaylistsStore = create<PlaylistsState>((set, get) => ({
  playlists: [],
  playlistSongs: [],
  seq: 0,
  load: IDLE_LOAD,

  createPlaylist(name, songIds = []) {
    const seq = get().seq + 1;
    const playlist: Playlist = {
      id: newId(),
      name,
      // Copied rather than referenced: the caller's array is theirs, and a playlist
      // holding a reference to it would change when they next mutated it.
      songIds: [...songIds],
      seq,
      createdAt: NOW_LABEL,
    };
    // Newest first, as the library is.
    set((s) => ({ playlists: [playlist, ...s.playlists], seq }));
    void playlistsApi.createPlaylist(name);
    return playlist;
  },

  rememberPlaylistSong(song) {
    set((s) => ({
      playlistSongs: s.playlistSongs.some((candidate) => candidate.id === song.id)
        ? s.playlistSongs.map((candidate) => (candidate.id === song.id ? song : candidate))
        : [song, ...s.playlistSongs],
    }));
  },

  renamePlaylist(id, name) {
    set((s) => ({ playlists: s.playlists.map((p) => (p.id === id ? { ...p, name } : p)) }));
    void playlistsApi.renamePlaylist(id, name);
  },

  deletePlaylist(id) {
    set((s) => ({ playlists: s.playlists.filter((p) => p.id !== id) }));
    void playlistsApi.deletePlaylist(id);
  },

  addSongToPlaylist(playlistId, songId) {
    const playlist = get().playlists.find((p) => p.id === playlistId);
    // Guarded rather than deduplicated on read: a playlist is an ordered list, so a
    // duplicate id would render the same track twice with the same React key, and
    // removing one of them would remove both.
    if (!playlist || playlist.songIds.includes(songId)) return;
    set((s) => ({
      playlists: s.playlists.map((p) =>
        p.id === playlistId ? { ...p, songIds: [...p.songIds, songId] } : p
      ),
    }));
    void playlistsApi.addSong(playlistId, songId);
  },

  removeSongFromPlaylist(playlistId, songId) {
    set((s) => ({
      playlists: s.playlists.map((p) =>
        p.id === playlistId ? { ...p, songIds: p.songIds.filter((id) => id !== songId) } : p
      ),
    }));
    void playlistsApi.removeSong(playlistId, songId);
  },

  moveSongInPlaylist(playlistId, songId, direction) {
    const playlist = get().playlists.find((p) => p.id === playlistId);
    if (!playlist) return;
    const from = playlist.songIds.indexOf(songId);
    const to = from + direction;
    // Silent at the ends rather than wrapping. "Move up" from the top position
    // meaning "go to the bottom" is not what any list means by it.
    if (from === -1 || to < 0 || to >= playlist.songIds.length) return;
    const songIds = [...playlist.songIds];
    songIds[from] = songIds[to];
    songIds[to] = songId;
    set((s) => ({
      playlists: s.playlists.map((p) => (p.id === playlistId ? { ...p, songIds } : p)),
    }));
    void playlistsApi.reorderSongs(playlistId, songIds);
  },

  removeSongEverywhere(songId) {
    const affected = get().playlists.filter((p) => p.songIds.includes(songId));
    if (affected.length === 0) return;
    set((s) => ({
      playlists: s.playlists.map((p) =>
        p.songIds.includes(songId) ? { ...p, songIds: p.songIds.filter((id) => id !== songId) } : p
      ),
    }));
    // One call per affected playlist, because the endpoint is scoped to a playlist.
    // A real backend deleting the song would cascade this itself and these calls
    // become unnecessary — noted rather than assumed, since a cascade that does not
    // happen leaves rows pointing at a deleted song.
    for (const p of affected) void playlistsApi.removeSong(p.id, songId);
  },

  async loadPlaylists() {
    set({ load: LOADING_LOAD });
    try {
      const playlists = await playlistsApi.getPlaylists();
      // Through `hydrate` for songsStore.loadSongs's reason: it is what moves `seq` past
      // the fetched set, and a load that bypassed it would give the next created playlist
      // an ordering key already taken.
      get().hydrate(playlists);
      set({ load: READY_LOAD });
    } catch (err) {
      // What is held is left alone — a failed refresh must not empty a list the user is
      // looking at. See songsStore.loadSongs.
      set({ load: failedLoad(err) });
    }
  },

  async loadPlaylistSongs() {
    try {
      get().hydratePlaylistSongs(await playlistsApi.getPlaylistSongs());
    } catch {
      // Playlist resolution keeps whatever it already had — a failed refresh must not make
      // saved public songs disappear from the user's own playlists.
    }
  },

  hydrate(playlists) {
    set({ playlists, seq: playlists.length });
  },

  hydratePlaylistSongs(songs) {
    set({ playlistSongs: songs });
  },

  reset() {
    set({ playlists: [], playlistSongs: [], seq: 0, load: IDLE_LOAD });
  },
}));

/** Looks up a playlist by id. */
export const selectPlaylistById = (playlists: Playlist[], id: string | null): Playlist | null =>
  (id ? playlists.find((p) => p.id === id) : null) ?? null;

/**
 * Resolves a playlist's ids into songs, in playlist order.
 *
 * Unresolvable ids are dropped rather than rendered as a blank row. Deleting a song
 * already removes it from every playlist (hooks/usePlaylists), so this is the second
 * line of defence — it is what keeps a stale id arriving from a future server, or
 * one left behind by a failed write, from becoming a row with no title.
 */
export function playlistSongs(playlist: Playlist, songs: Song[]): Song[] {
  return playlist.songIds
    .map((id) => songs.find((s) => s.id === id))
    .filter((s): s is Song => s !== undefined);
}

/**
 * Merges the owner library with extra playlist-referenced songs, keyed by song id.
 *
 * Owner-library rows win on collision: when the same song is in both sets, the library copy is
 * the one that should carry the freshest owner-scoped fields.
 */
export function playlistSongLibrary(librarySongs: Song[], playlistSongsOnly: Song[]): Song[] {
  const byId = new Map<string, Song>();
  for (const song of playlistSongsOnly) byId.set(song.id, song);
  for (const song of librarySongs) byId.set(song.id, song);
  return Array.from(byId.values());
}

/** Playlists containing a given song. Drives the checkmarks in "Add to playlist". */
export function playlistsWithSong(playlists: Playlist[], songId: string): Playlist[] {
  return playlists.filter((p) => p.songIds.includes(songId));
}

/**
 * How many playlists each song is in, keyed by song id — the app's save count.
 *
 * SAVING IS ADDING TO A PLAYLIST here: it is what the lyrics dialog's Save button and the
 * Library card's "Add to a playlist" both do. So the number of times a song has been saved
 * is not a counter anybody has to maintain, it is a fact this state already holds, and this
 * is the one place it is counted. Nothing is written onto the song.
 *
 * A song absent from the result has zero saves. Computed as a whole map rather than
 * per-song, because the caller that needs it (hooks/useShowcase, for the Featured ranking)
 * needs every song's count at once and `playlistsWithSong` per song would be a scan of
 * every playlist per song.
 */
export function songSaveCounts(playlists: Playlist[]): Record<string, number> {
  const counts: Record<string, number> = {};
  const saved = new Set<string>();
  for (const playlist of playlists) {
    // One user's save counts once however many playlists they file it into. The app still
    // allows several playlists to reference the song; only the engagement count is deduped.
    for (const id of playlist.songIds) saved.add(id);
  }
  for (const id of saved) counts[id] = 1;
  return counts;
}

/**
 * The one-line summary under a playlist's name: "3 tracks · 9:45".
 *
 * Composed from parseLength and formatTime rather than a new duration format, so a
 * playlist's total reads the same way a song's length does. Counts and sums only
 * songs that resolve, for the reason playlistSongs drops the others: a total that
 * included a deleted track would not match the list underneath it.
 */
export function playlistSummary(playlist: Playlist, songs: Song[]): string {
  const tracks = playlistSongs(playlist, songs);
  const count = `${tracks.length} ${tracks.length === 1 ? 'track' : 'tracks'}`;
  if (tracks.length === 0) return count;
  const total = tracks.reduce((sum, s) => sum + parseLength(s.length), 0);
  return `${count} · ${formatTime(total)}`;
}
